import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Palette, Users, MapPin, Award, Share2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { utmPayload } from '../lib/tracking';
import ContestEntryForm from './ContestEntryForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const useCountdown = (targetIso) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!targetIso) return null;
  const t = new Date(`${targetIso}T09:00:00-04:00`).getTime(); // 9am ET on the day
  const diff = t - now;
  if (diff <= 0) return { expired: true, d: 0, h: 0, m: 0, s: 0 };
  return {
    expired: false,
    d: Math.floor(diff / (1000 * 60 * 60 * 24)),
    h: Math.floor((diff / (1000 * 60 * 60)) % 24),
    m: Math.floor((diff / (1000 * 60)) % 60),
    s: Math.floor((diff / 1000) % 60),
  };
};

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  city: '',
  role: 'artist',
  instagram: '',
  marketingConsent: true,
  referralSource: '',
};

const WineOfTheYear = () => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/contest/config`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const countdown = useCountdown(cfg?.entryOpen);
  const stage = cfg?.stage || 'pre_launch';
  const submissionsOpen = cfg?.submissionsOpen || stage === 'entry_open';
  const totalSignups = cfg?.stats?.totalSignups ?? 0;
  const artistSignups = cfg?.stats?.artistSignups ?? 0;

  const stageMessaging = useMemo(() => {
    switch (stage) {
      case 'entry_open':
        return {
          eyebrow: 'SUBMISSIONS ARE NOW OPEN',
          headline: 'Your Art Could Be Our 2026 Wine of the Year.',
          sub: "We're looking for local artists to help us create something uniquely ours. Submit your original artwork for the chance to have your design featured on The Lokal's 2026 Wine of the Year — bottled right here in Fenelon Falls. Winning artwork will become part of The Lokal's story and will be featured on bottles available to our clients for years to come.",
          cta: 'Enter the Contest',
        };
      case 'voting':
        return {
          eyebrow: 'VOTING IS OPEN',
          headline: 'Meet the Artists & Vote',
          sub: 'Help choose our 2026 Wine of the Year label.',
          cta: 'Vote for Your Favourite',
        };
      case 'winner_pending':
        return {
          eyebrow: 'VOTING IS CLOSED',
          headline: 'Winner Coming Soon',
          sub: 'Thank you to every artist and voter — the winner will be announced shortly.',
          cta: 'Join the Announcement List',
        };
      default:
        return {
          eyebrow: 'ENTRIES OPEN SEPT 15, 2026',
          headline: 'Your Art. Our Next Bottle.',
          sub: "We're searching for a local artist to design The Lokal Winery's 2026 Wine of the Year — and we want you on the list the moment entries open.",
          cta: 'Get Notified When Entries Open',
        };
    }
  }, [stage]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    if (!form.firstName.trim()) return setError('Please share your first name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setError('Enter a valid email.');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contest/interest`, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim() || null,
        email: form.email.trim().toLowerCase(),
        city: form.city.trim() || null,
        role: form.role,
        instagram: form.instagram.trim() || null,
        marketingConsent: form.marketingConsent,
        referralSource: form.referralSource.trim() || null,
        ...utmPayload(),
      });
      setResult(data);
      window.scrollTo({ top: document.getElementById('signup-confirm')?.offsetTop || 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('signup-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToDetails = () => {
    document.getElementById('woty-artist-value')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'noopener,noreferrer');
  };
  const shareViaEmail = () => {
    const subject = encodeURIComponent("Fenelon Falls artist? Get your work on a wine bottle.");
    const body = encodeURIComponent(
      `Hey — The Lokal Winery is running a contest to design their 2026 Wine of the Year label. ` +
      `Winning artwork goes on the bottle, 15 bottles for the artist, and the label stays available for future orders. ` +
      `Sign up here: ${window.location.href}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-[#fdfbf5]" data-testid="woty-page">
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(rgba(35,20,10,0.55), rgba(35,20,10,0.75)), url('https://images.unsplash.com/photo-1560493676-04071c5f467b?auto=format&fit=crop&w=1920&q=80') center/cover",
          color: '#F5EBDC',
        }}
        data-testid="woty-hero"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28">
          <p className="text-[11px] sm:text-xs tracking-[3px] font-semibold" style={{ color: '#FDB913' }}>
            THE LOKAL WINERY · {stageMessaging.eyebrow}
          </p>
          <h1
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {stageMessaging.headline}
          </h1>
          {submissionsOpen && (
            <p
              className="mt-4 text-xl sm:text-2xl font-semibold"
              style={{ color: '#FDB913', fontFamily: "'Playfair Display', Georgia, serif" }}
              data-testid="woty-submissions-open"
            >
              Submissions Are Now Open.
            </p>
          )}
          <p className="mt-6 text-base sm:text-lg lg:text-xl max-w-2xl leading-relaxed opacity-95">
            {stageMessaging.sub}
          </p>

          {/* Countdown — always visible in pre-launch */}
          {stage === 'pre_launch' && countdown && !countdown.expired && (
            <div
              className="mt-8 inline-flex flex-wrap gap-3 sm:gap-4 items-center rounded-2xl backdrop-blur-md px-5 sm:px-7 py-4"
              style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(253,185,19,0.35)' }}
              data-testid="woty-countdown"
            >
              <span className="text-[10px] tracking-[2px] uppercase opacity-80 self-center">
                Entries open in
              </span>
              {[
                { v: countdown.d, l: 'days' },
                { v: countdown.h, l: 'hrs' },
                { v: countdown.m, l: 'min' },
                { v: countdown.s, l: 'sec' },
              ].map((u) => (
                <div key={u.l} className="text-center min-w-[52px]">
                  <div className="text-2xl sm:text-3xl font-bold leading-none">
                    {String(u.v).padStart(2, '0')}
                  </div>
                  <div className="text-[9px] tracking-[1.5px] uppercase opacity-80">{u.l}</div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              onClick={scrollToForm}
              data-testid="woty-hero-cta"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 sm:py-5 rounded-md font-bold text-base sm:text-lg text-gray-900 shadow-xl hover:brightness-110 hover:scale-[1.02] transition uppercase tracking-wide"
              style={{ background: '#FDB913' }}
            >
              {stageMessaging.cta} →
            </button>
            {submissionsOpen ? (
              <button
                onClick={scrollToDetails}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md font-semibold border-2 hover:bg-white/10 transition"
                style={{ borderColor: 'rgba(245,235,220,0.55)', color: '#F5EBDC' }}
                data-testid="woty-hero-details"
              >
                View Contest Details
              </button>
            ) : (
              <button
                onClick={shareOnFacebook}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md font-semibold border-2 hover:bg-white/10 transition"
                style={{ borderColor: 'rgba(245,235,220,0.55)', color: '#F5EBDC' }}
                data-testid="woty-hero-share"
              >
                <Share2 className="w-4 h-4" /> Share the Contest
              </button>
            )}
          </div>

          <button
            onClick={() => navigate('/wine-of-the-year/gallery')}
            data-testid="woty-hero-gallery-link"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold underline-offset-4 hover:underline"
            style={{ color: '#FDB913' }}
          >
            <Palette className="w-4 h-4" /> Meet the 2026 artists →
          </button>

          <div className="mt-10 flex flex-wrap gap-4 text-sm">
            <Pill icon={<CheckCircle2 className="w-4 h-4" />}>Free to enter</Pill>
            <Pill icon={<Users className="w-4 h-4" />}>Community voted</Pill>
            <Pill icon={<MapPin className="w-4 h-4" />}>Fenelon Falls, Ontario</Pill>
          </div>

          {totalSignups > 0 && (
            <p className="mt-6 text-sm opacity-90" data-testid="woty-social-proof">
              <strong>{totalSignups}</strong> already on the list
              {artistSignups > 0 ? <> · <strong>{artistSignups}</strong> local artists</> : null}.
            </p>
          )}
        </div>
      </section>

      {/* Timeline strip */}
      <section className="bg-white border-y" style={{ borderColor: '#f0e4c8' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 text-center">
          <TimelineStep
            step="01"
            title="Entries Open"
            date={`${cfg?.entryOpen || 'Sept 15'} – ${cfg?.entryClose || 'Oct 5, 2026'}`}
            active={stage === 'entry_open'}
          />
          <TimelineStep
            step="02"
            title="Public Voting"
            date={`${cfg?.votingOpen || 'Oct 15'} – ${cfg?.votingClose || 'Nov 15, 2026'}`}
            active={stage === 'voting'}
          />
          <TimelineStep
            step="03"
            title="Winner Announced"
            date="End of November 2026"
            active={stage === 'winner_pending'}
          />
        </div>
      </section>

      {/* Value prop for artists */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20" data-testid="woty-artist-value">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[2px] font-semibold" style={{ color: '#722F37' }}>
            WHY ENTER
          </p>
          <h2
            className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            More Than a Contest.<br className="hidden sm:block" /> Your Art On Our 2026 Signature Wine.
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-gray-600 text-base sm:text-lg">
            Local artists deserve exposure that lasts longer than a gallery show.
            The winning artwork becomes the official label for our 2026 Wine of the Year —
            and stays available as a label option for every customer who orders that
            wine in the years ahead.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <ValueCard
            icon={<Palette className="w-6 h-6" />}
            title="Your art on the signature label"
            body="Your design becomes the official label of The Lokal Winery's 2026 Wine of the Year — your name, your story, your artwork on the bottle."
          />
          <ValueCard
            icon={<Award className="w-6 h-6" />}
            title={`${cfg?.prizeBottles ?? 15} bottles with your label`}
            body="Your choice of red or white — bottled with your winning label. Yours to keep, gift, or share with the people in your corner."
          />
          <ValueCard
            icon={<Users className="w-6 h-6" />}
            title="A permanent label option"
            body="Long after the contest ends, any customer ordering the 2026 signature wine can choose your winning label — your artwork keeps travelling for years."
          />
          <ValueCard
            icon={<Share2 className="w-6 h-6" />}
            title="A full artist profile"
            body="A story-first profile on our site and shared across our social media — your inspiration, your community, and where to find more of your work."
          />
          <ValueCard
            icon={<MapPin className="w-6 h-6" />}
            title="Promoted everywhere we go"
            body="Featured on our homepage, our social channels, our newsletter, and every campaign we run around the winning wine."
          />
        </div>
      </section>

      {/* Fenelon Falls storytelling */}
      <section
        className="relative"
        style={{
          background:
            "linear-gradient(rgba(35,20,10,0.68), rgba(35,20,10,0.82)), url('https://customer-assets.emergentagent.com/job_lokal-landing/artifacts/gir44yyn_Website%20Wedding%20Pictures%20%288%29.svg') center/cover",
          color: '#F5EBDC',
        }}
        data-testid="woty-fenelon"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <p className="text-[11px] tracking-[3px] font-semibold" style={{ color: '#FDB913' }}>
            CREATED HERE · SHARED EVERYWHERE
          </p>
          <h2
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Put Fenelon Falls on the Map.
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed opacity-95">
            Fenelon Falls is built by artists, makers, small business owners, and
            people who care where they live. The 2026 Wine of the Year is our
            invitation to the community: help us bottle a piece of this place
            and share it with everyone who pours a glass.
          </p>
          <p className="mt-4 text-base sm:text-lg leading-relaxed opacity-95">
            A local artist's canvas. A piece of Fenelon Falls. A story people can
            take home and share.
          </p>
        </div>
      </section>

      {/* Signup form */}
      <section
        id="signup-form"
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20"
        data-testid="woty-signup-section"
      >
        {submissionsOpen ? (
          <ContestEntryForm cfg={cfg} />
        ) : !result ? (
          <div className="bg-white rounded-2xl shadow-xl border p-6 sm:p-10" style={{ borderColor: '#f0e4c8' }}>
            <div className="text-center mb-8">
              <p className="text-xs tracking-[2px] font-semibold" style={{ color: '#722F37' }}>
                RESERVE YOUR SPOT
              </p>
              <h3
                className="mt-2 text-2xl sm:text-3xl font-bold text-gray-900"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Get Notified the Moment Entries Open
              </h3>
              <p className="mt-3 text-gray-600 text-sm sm:text-base">
                We'll email you the second the submission form goes live on {cfg?.entryOpen || 'Sept 15'}. No spam — just this contest.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-4" data-testid="woty-form">
              {/* Role toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">I'm signing up as… *</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { v: 'artist', label: 'A local artist', hint: 'I want to enter my work' },
                    { v: 'supporter', label: 'A supporter', hint: "I want to vote & follow along" },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      className={`cursor-pointer rounded-lg border-2 p-3 sm:p-4 transition ${
                        form.role === opt.v
                          ? 'border-[#FDB913] bg-[#FFFBEA]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`woty-role-${opt.v}`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={opt.v}
                        className="sr-only"
                        checked={form.role === opt.v}
                        onChange={() => update('role', opt.v)}
                      />
                      <div className="font-semibold text-gray-900 text-sm sm:text-base">{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.hint}</div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  id="fn"
                  label="First name *"
                  value={form.firstName}
                  onChange={(v) => update('firstName', v)}
                  testId="woty-firstname"
                />
                <Field
                  id="ln"
                  label="Last name"
                  value={form.lastName}
                  onChange={(v) => update('lastName', v)}
                  testId="woty-lastname"
                />
              </div>

              <Field
                id="em"
                label="Email *"
                type="email"
                value={form.email}
                onChange={(v) => update('email', v)}
                testId="woty-email"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  id="ci"
                  label="City / community"
                  value={form.city}
                  onChange={(v) => update('city', v)}
                  placeholder="Fenelon Falls, ON"
                  testId="woty-city"
                />
                <Field
                  id="ig"
                  label={form.role === 'artist' ? 'Instagram / portfolio (optional)' : 'How did you hear about us?'}
                  value={form.role === 'artist' ? form.instagram : form.referralSource}
                  onChange={(v) =>
                    update(form.role === 'artist' ? 'instagram' : 'referralSource', v)
                  }
                  placeholder={form.role === 'artist' ? '@yourhandle' : 'Instagram, friend, in-store…'}
                  testId="woty-extra"
                />
              </div>

              <label className="flex items-start gap-3 pt-2 cursor-pointer" data-testid="woty-consent-row">
                <input
                  type="checkbox"
                  checked={form.marketingConsent}
                  onChange={(e) => update('marketingConsent', e.target.checked)}
                  className="mt-1 h-4 w-4"
                />
                <span className="text-sm text-gray-600 leading-relaxed">
                  Send me updates about this contest and future community campaigns.
                  You can unsubscribe any time.
                </span>
              </label>

              {error && (
                <div className="text-sm text-red-600" data-testid="woty-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="woty-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md font-bold text-gray-900 shadow-lg hover:brightness-110 transition disabled:opacity-50"
                style={{ background: '#FDB913' }}
              >
                {submitting ? 'Saving…' : 'Save My Spot'}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Free to enter · No purchase required · You can opt out any time
              </p>
              <p className="text-xs text-gray-400 text-center leading-relaxed" data-testid="woty-nonbinding">
                By joining the list you're only asking to be notified — full contest rules
                will be shared when the entry form goes live on {cfg?.entryOpen || 'Sept 15'}.
              </p>
            </form>
          </div>
        ) : (
          <div
            id="signup-confirm"
            className="bg-white rounded-2xl shadow-xl border p-8 sm:p-10 text-center"
            style={{ borderColor: '#f0e4c8' }}
            data-testid="woty-thankyou"
          >
            <div className="text-5xl mb-4">🎨🍷</div>
            <h3
              className="text-2xl sm:text-3xl font-bold text-gray-900"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {result.duplicate ? "You're already on the list!" : "You're in!"}
            </h3>
            <p className="mt-3 text-gray-600 text-base">
              {result.duplicate
                ? "We've refreshed your details. We'll email you the moment entries open."
                : "Check your inbox — we've sent a confirmation. We'll email you the second submissions go live on " +
                  (cfg?.entryOpen || 'Sept 15') + '.'}
            </p>

            <div className="mt-8 space-y-3">
              <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Help us reach every local artist
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={shareOnFacebook}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold border-2 transition hover:bg-gray-50"
                  style={{ borderColor: '#FDB913', color: '#722F37' }}
                  data-testid="woty-share-facebook"
                >
                  <Share2 className="w-4 h-4" /> Share on Facebook
                </button>
                <button
                  onClick={shareViaEmail}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold text-gray-900 hover:brightness-110 transition"
                  style={{ background: '#FDB913' }}
                  data-testid="woty-share-email"
                >
                  <Share2 className="w-4 h-4" /> Email a friend
                </button>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t text-sm text-gray-500" style={{ borderColor: '#f0e4c8' }}>
              While you wait — <button
                onClick={() => navigate('/wines')}
                className="text-[#722F37] font-semibold underline hover:no-underline"
              >explore our wines</button> or{' '}
              <a href="https://klone.thelokal.ca" target="_blank" rel="noopener noreferrer"
                className="text-[#722F37] font-semibold underline hover:no-underline">try Klone Cellar</a>.
            </div>
          </div>
        )}
      </section>

      {/* FAQ mini */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="woty-faq">
        <h3
          className="text-2xl font-bold text-gray-900 text-center"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Quick answers
        </h3>
        <div className="mt-8 space-y-3">
          {FAQ.map((f, i) => (
            <details
              key={i}
              className="bg-white rounded-xl border p-5 group"
              style={{ borderColor: '#f0e4c8' }}
            >
              <summary className="cursor-pointer font-semibold text-gray-900 flex justify-between items-center list-none">
                {f.q}
                <span className="text-[#FDB913] group-open:rotate-45 transition">+</span>
              </summary>
              <p className="mt-3 text-gray-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
};

const FAQ = [
  {
    q: 'Is it really free to enter?',
    a: 'Yes — completely free. No purchase, no entry fee, no strings attached.',
  },
  {
    q: 'Who can enter?',
    a: 'Any artist. We built this to celebrate Fenelon Falls and Kawartha Lakes creators, but talent from surrounding communities is warmly welcomed too.',
  },
  {
    q: 'What happens to my artwork if I enter?',
    a: "If your design is selected as the winning entry, you grant The Lokal Winery the right to reproduce your artwork on the 2026 Wine of the Year label, related packaging, and marketing across our website, social channels, and in-store — with you credited as the artist and included in how your story is shared. Non-winning entries remain fully yours; we won't use them without your permission.",
  },
  {
    q: 'What does the winner receive?',
    a: '15 bottles of wine (your choice of red or white) featuring your winning label, a permanent label option so customers can keep choosing your artwork on future orders of the 2026 signature wine, and a full artist profile shared across our website and social media.',
  },
  {
    q: "What happens next?",
    a: "You'll get a confirmation email now, and a launch email the moment submissions open on Sept 15, 2026. Entries close Oct 5, voting runs Oct 15 – Nov 15, and the winner is announced at the end of November.",
  },
];

const Pill = ({ icon, children }) => (
  <span
    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm backdrop-blur-sm"
    style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(245,235,220,0.35)' }}
  >
    {icon} {children}
  </span>
);

const ValueCard = ({ icon, title, body }) => (
  <div
    className="rounded-2xl bg-white p-6 border shadow-sm hover:shadow-md transition"
    style={{ borderColor: '#f0e4c8' }}
  >
    <div
      className="inline-flex items-center justify-center w-10 h-10 rounded-lg mb-4"
      style={{ background: '#FFFBEA', color: '#722F37' }}
    >
      {icon}
    </div>
    <h3 className="font-bold text-gray-900 text-lg">{title}</h3>
    <p className="mt-2 text-gray-600 leading-relaxed">{body}</p>
  </div>
);

const TimelineStep = ({ step, title, date, active }) => (
  <div className={`transition ${active ? 'opacity-100' : 'opacity-70'}`}>
    <div
      className="inline-flex items-center justify-center w-10 h-10 rounded-full font-bold mb-2"
      style={{
        background: active ? '#FDB913' : '#F5EBDC',
        color: active ? '#2a1a10' : '#6B4A32',
      }}
    >
      {step}
    </div>
    <h4 className="font-bold text-gray-900">{title}</h4>
    <p className="text-sm text-gray-500">{date}</p>
  </div>
);

const Field = ({ id, label, value, onChange, type = 'text', placeholder, testId }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}
    </label>
    <input
      id={id}
      data-testid={testId}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#FDB913] focus:ring-2 focus:ring-[#FDB913] focus:ring-opacity-30 outline-none transition"
    />
  </div>
);

export default WineOfTheYear;
